import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "../lib/cn";

/**
 * Checkbox, design-system spec: 20x20, 5px radius, teal-700 fill when
 * checked, 1.5px border, animated check glyph drawn as a CSS rotation.
 *
 * Native `<input type="checkbox">` underneath (sr-only) so the
 * checkbox toggles via space/enter and forms register it correctly;
 * the visual chrome is the labelled `<span>` next to it.
 */
export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange" | "checked"> {
  checked: boolean;
  onCheckedChange?: (next: boolean) => void;
  label?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, onCheckedChange, label, className, ...rest },
  ref,
) {
  return (
    <label
      className={cn(
        "relative inline-grid h-5 w-5 shrink-0 cursor-pointer place-items-center rounded-sm border-[1.5px] [transition:background-color_180ms_var(--ease-out-expo),border-color_180ms_var(--ease-out-expo)]",
        checked ? "border-teal-700 bg-teal-700" : "border-gray-400 bg-white",
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(e) => onCheckedChange?.(e.currentTarget.checked)}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
        {...rest}
      />
      {checked ? (
        <span
          aria-hidden="true"
          className="block h-[6px] w-[10px] -translate-y-[1px] -rotate-45 border-b-[2px] border-l-[2px] border-white"
        />
      ) : null}
    </label>
  );
});
